import React, { useMemo } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  AnalyticsGranularity,
  ClicksSeriesResponse,
} from "../../api/analyticsAdmin";
import { formatBucketTick } from "../../helpers/searchTrend";
import { formatInteger } from "../../helpers/formatNumber";
import SectionBody from "./SectionBody";
import { SectionState } from "./useAnalyticsStream";

const LINES: { key: string; label: string; color: string }[] = [
  { key: "opens", label: "Opens", color: "#503595" },
  { key: "outbounds", label: "Outbound", color: "#0ea5e9" },
  { key: "convertedPairs", label: "Conversions", color: "#10b981" },
];

const GRANULARITIES: AnalyticsGranularity[] = ["Day", "Week", "Month"];

const byBucketStart = (a: { bucketStart: string }, b: { bucketStart: string }) =>
  a.bucketStart < b.bucketStart ? -1 : a.bucketStart > b.bucketStart ? 1 : 0;

const ClicksFunnelTrend: React.FC<{
  section: SectionState<ClicksSeriesResponse>;
  granularity: AnalyticsGranularity;
  onGranularityChange: (granularity: AnalyticsGranularity) => void;
}> = ({ section, granularity, onGranularityChange }) => {
  const rows = useMemo(() => {
    const points =
      section.status === "ok" ? section.data.series?.[0]?.points ?? [] : [];
    return [...points].sort(byBucketStart);
  }, [section]);

  return (
    <section className="mb-12">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-lg font-medium">Trend over time</h3>
        <div className="flex gap-1">
          {GRANULARITIES.map((g) => (
            <button
              key={g}
              onClick={() => onGranularityChange(g)}
              className={`px-3 py-1.5 text-sm rounded-lg border ${
                granularity === g
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-gray-300 text-gray-600 hover:bg-gray-50"
              }`}
            >
              {g}
            </button>
          ))}
        </div>
      </div>

      <SectionBody
        section={section}
        isEmpty={() => rows.length === 0}
        emptyMessage="No clicks activity for this range."
      >
        {() => (
          <div className="bg-white rounded-lg shadow p-6">
            <div style={{ height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={rows}
                  margin={{ top: 5, right: 24, left: 8, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="bucketStart"
                    tickFormatter={(v: string) => formatBucketTick(v, granularity)}
                  />
                  <YAxis tickFormatter={formatInteger} />
                  <Tooltip formatter={(v: number) => formatInteger(v)} />
                  <Legend />
                  {LINES.map((l) => (
                    <Line
                      key={l.key}
                      type="monotone"
                      dataKey={l.key}
                      name={l.label}
                      stroke={l.color}
                      dot={false}
                      connectNulls
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </SectionBody>
    </section>
  );
};

export default ClicksFunnelTrend;
